import { ApplyEngagementBlueprintModal } from "PFApp/activities/engagement_blueprint/components/apply_engagement_blueprint_modal";
import { useActivityPermissions } from "PFApp/activities/hooks";
import { useActivityShowContext } from "PFApp/activities/show/context/activity_show_context";
import { useCurrentActivity } from "PFApp/activities/show/hooks/use_current_activity";
import RemoveOwners from "PFApp/activities/show/parts/remove_owners";
import { RequestHelpModal } from "PFApp/activities/show/parts/request_help_modal";
import ActivityActions from "PFApp/components/activity_actions/activity_actions";
import { DropdownOption } from "PFComponents/dropdown";
import useIsFeatureEnabled from "PFCore/helpers/use_is_feature_enabled";
import { useActivityInvalidate } from "PFCore/hooks/queries/activity";
import { useEngagementsInvalidate } from "PFCore/hooks/queries/engagements";
import { useCurrentProfile } from "PFCore/hooks/queries/profile/use_current_profile";
import { useRolesInvalidate } from "PFCore/hooks/queries/roles/use_roles_invalidate";
import { useShortlistsInvalidate } from "PFCore/hooks/queries/shortlists/use_shortlists_invalidate";
import { FeatureFlag } from "PFCore/types";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

export const ActivityShowMenuButton = () => {
  const { data: currentProfile } = useCurrentProfile();
  const { invalidate: invalidateActivities, invalidateVacancies } = useActivityInvalidate();
  const { invalidateShortlists } = useShortlistsInvalidate();
  const activity = useCurrentActivity();
  const { invalidateRoles, invalidateAuditRoles } = useRolesInvalidate();
  const { invalidateAll: invalidateEngagements } = useEngagementsInvalidate();
  const { canCreateRoles } = useActivityPermissions(activity);
  const { isEngagement, isAudit, isRole } = useActivityShowContext();
  const isEnabled = useIsFeatureEnabled();
  const isEngagementBlueprintsEnabled = isEnabled(FeatureFlag.EngagementBlueprints);

  const [showRemoveCoOwnersModal, setShowRemoveCoOwnersModal] = useState(false);
  const [showRequestHelpModal, setShowRequestHelpModal] = useState(false);
  const [showApplyBlueprintModal, setShowApplyBlueprintModal] = useState(false);
  const { t } = useTranslation("activities");

  const isClosed = activity.state === "complete";
  const canAddRoles = !isClosed && isEngagement && canCreateRoles;

  const additionalOptions = useMemo(() => {
    const options: Array<DropdownOption & { id: string }> = [];

    if (!activity.coowned) {
      return options;
    }

    const removableOwners = activity.owners.filter(
      (owner) => ![currentProfile.id, activity.profile.id].includes(owner.profile.id)
    );

    if (removableOwners.length > 0) {
      options.push({
        id: "remove-co-owner",
        displayElement: t("show.details.removeCoOwner"),
        item: () => setShowRemoveCoOwnersModal(true)
      });
    }
    options.push({
      id: "request_help",
      displayElement: t("show.details.requestHelp"),
      item: () => setShowRequestHelpModal(true)
    });
    if (isEngagementBlueprintsEnabled && canAddRoles) {
      options.push({
        id: "apply_blueprint",
        displayElement: t("engagementBlueprint.applyEngagementBlueprint"),
        item: () => setShowApplyBlueprintModal(true)
      });
    }

    return options;
  }, [activity, currentProfile, canAddRoles, isEngagementBlueprintsEnabled, t]);

  const handleCloseActivitySuccess = () => {
    invalidateActivities([activity.id]);
    invalidateVacancies(activity.id);
    invalidateShortlists(activity.id);

    if (isRole) {
      if (isAudit) {
        invalidateAuditRoles();
      } else {
        invalidateRoles();
      }
    }
    if (isEngagement) {
      invalidateEngagements();
    }
  };

  return (
    <div style={{ display: "inline-block" }}>
      <ActivityActions
        activity={activity}
        handlePublish={() => invalidateActivities([activity.id])}
        additionalOptions={additionalOptions}
        dropDownStyle={additionalOptions.length > 0 ? { width: 160, maxHeight: "unset" } : undefined}
        handleCloseActivitySuccess={handleCloseActivitySuccess}
        qaId="activity-actions-button"
        variant="button"
        actionBlacklist={["show_history"]}
      />
      {showRemoveCoOwnersModal && <RemoveOwners onClose={() => setShowRemoveCoOwnersModal(false)} />}

      {showRequestHelpModal && (
        <RequestHelpModal activity={activity} onClose={() => setShowRequestHelpModal(false)} />
      )}
      {showApplyBlueprintModal && (
        <ApplyEngagementBlueprintModal
          activityId={activity.id}
          onClose={() => setShowApplyBlueprintModal(false)}
        />
      )}
    </div>
  );
};
